import React, { useState } from 'react';
import { Alert, Button, Container, IconButton, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Link, useLocation } from 'react-router-dom';
import { useOutstandingBalance } from '../../contexts/OutstandingBalanceContext';
import { balanceProfilePath, formatMoney } from '../../utils/outstandingBalance';

export const DISMISS_KEY = 'outstandingBalanceBannerDismissed';

const isDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

/** Tells a signed-in member they have an overdue balance. Dismissed per browser session. */
const OutstandingBalanceBanner = () => {
  const location = useLocation();
  const { balance, userTenantId } = useOutstandingBalance();
  const [dismissed, setDismissed] = useState(isDismissed);

  const total = balance?.totalOverdueCents || 0;
  const profilePath = balanceProfilePath(userTenantId);
  const onOwnProfile = userTenantId && location.pathname === `/player/${userTenantId}`;

  if (dismissed || total <= 0 || !profilePath || onOwnProfile) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Storage unavailable — still hide for this render tree.
    }
    setDismissed(true);
  };

  const count = balance.invoices?.length || 0;

  return (
    <Container maxWidth="lg" sx={{ mt: 2, px: { xs: 1, sm: 2 } }}>
      <Alert
        severity="error"
        action={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Button component={Link} to={profilePath} color="inherit" size="small" sx={{ whiteSpace: 'nowrap' }}>
              View &amp; pay
            </Button>
            <IconButton aria-label="Dismiss balance reminder" color="inherit" size="small" onClick={handleDismiss}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        }
        sx={{ alignItems: 'center' }}
      >
        You have an overdue balance of <strong>{formatMoney(total)}</strong>
        {count > 1 ? ` across ${count} invoices` : ''}.
      </Alert>
    </Container>
  );
};

export default OutstandingBalanceBanner;
